import { useEffect, useState, type ReactNode } from 'react';
import { Eye, Lock, RefreshCw } from 'lucide-react';
import type { ReviewLeaseState } from './useReviewLease';

/**
 * What the fields column says about the open document's lease (ADR-0009): who is reviewing it
 * while it is theirs, with Take over once their lease is stale; and "this document changed" after
 * a save Paige refused; that the view is read-only (staff under a read-only support grant).
 * Nothing while the document is mine and every save went through.
 */
export function ReviewLeaseNotice({ lease }: { lease: ReviewLeaseState }): ReactNode {
  const now = useClock(lease.status === 'held' ? 5_000 : null);
  if (lease.conflict && lease.conflict.code === 'review_stale') {
    return (
      <div className="paige-review-notice" role="status">
        <RefreshCw size={14} />
        <span>This document changed; here is the latest.</span>
        <button type="button" className="quiet-button" onClick={lease.dismissConflict}>
          OK
        </button>
      </div>
    );
  }
  if (lease.status === 'viewing')
    return (
      <div className="paige-review-notice paige-review-held" role="status">
        <Eye size={14} />
        <span>Read-only view: you can read this document, and nothing here can be changed</span>
      </div>
    );
  if (lease.status !== 'held') return null;
  const name = lease.holder?.name ?? 'Another reviewer';
  const stale = lease.takeOverAt !== null && lease.takeOverAt <= now;
  return (
    <div className="paige-review-notice paige-review-held" role="status">
      <Lock size={14} />
      <span>{name} is reviewing this</span>
      {stale && (
        <button type="button" className="quiet-button" onClick={() => void lease.takeOver()}>
          Take over
        </button>
      )}
    </div>
  );
}

/** The time, re-read every `everyMs` while set, so "stale" flips without a reload. */
function useClock(everyMs: number | null): number {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    if (everyMs === null) return undefined;
    const timer = window.setInterval(() => setNow(Date.now()), everyMs);
    return () => window.clearInterval(timer);
  }, [everyMs]);
  return now;
}
